import React, { useState, useRef, useEffect } from 'react'
import styles from './SimpleLanguageSelector.module.scss'
import earthIcon from '@assets/img/earth.png'
import downIcon from '@assets/img/down-thick.png'
import { languageOptions, languageLabelMap } from '@/config'

interface SimpleLanguageSelectorProps {
  selectedLanguage: string
  onLanguageChange: (lang: string) => void
  className?: string
  // 可选：外部传入的语言选项列表，默认使用共享的 languageOptions（agent 页面不传则行为不变）
  options?: { label: string; value: string }[]
}

const SimpleLanguageSelector: React.FC<SimpleLanguageSelectorProps> = ({
  selectedLanguage,
  onLanguageChange,
  className = '',
  options
}) => {
  // 未传入 options 时回退到共享配置，保证 agent 页面零改动
  const languageList = options ?? languageOptions
  const [isOpen, setIsOpen] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setIsOpen(true)
    setIsAnimating(true)
  }

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => {
      setIsAnimating(false)
      setTimeout(() => {
        setIsOpen(false)
      }, 300)
    }, 200)
  }

  const handleLanguageSelect = (lang: string) => {
    onLanguageChange(lang)
    setIsOpen(false)
  }

  const getLanguageName = (lang: string) => {
    return languageLabelMap[lang] || lang
  }

  return (
    <div
      className={`${styles.languageSelector} ${className}`}
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.selector}>
        <img src={earthIcon} alt="earth" />
        <span>{getLanguageName(selectedLanguage)}</span>
        <img src={downIcon} alt="down" className={styles.downArrow} />
      </div>

      {isOpen && (
        <div
          className={`${styles.dropdown} ${isAnimating ? styles.fadeIn : styles.fadeOut}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className={styles.dropdownInner}>
            {languageList.map((lang, index) => (
              <div
                key={lang.value}
                className={`${styles.languageItem} ${selectedLanguage === lang.value ? styles.active : ''} ${index === 0 ? styles.firstItem : ''}`}
                onClick={() => handleLanguageSelect(lang.value)}
              >
                {lang.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default SimpleLanguageSelector

